import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { makeRequest } from '../../../../../utils/makeRequest';
import AutoSizer from 'react-virtualized-auto-sizer';
import VirtualizedList from '../../../../../components/VirtualizedList';
import PopupMessage from './components/PopupMessage';
import TableMultiLineText from '../../../../../components/TableMultiLineText';

function PopupMessages({ setTabSpinner }) {
  const { apiOriginAggregator } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [aggregators, setAggregators] = useState([]);

  const [popupList, setPopupList] = useState([]);
  const [popupMessage, setPopupMessage] = useState(null);

  const getAggregators = useCallback(async () => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginAggregator}/get`,
      method: 'GET',
      params: {
        sortBy: 'name',
        sortOrder: 'asc',
        statusFilter: '',
        searchText: '',
        page: 0,
        perPage: 1000000,
      },
    };

    try {
      const response = await makeRequest(config);

      setAggregators(response);
    } catch (error) {
      toast.error(`Failed getting aggregators: ${error.message}`);
    }
  }, [apiOriginAggregator, getAccessTokenSilently]);

  const popupRequest = useCallback(
    async ({ disableSpinner = true, url, method = 'GET', payload, onSuccess }) => {
      setTabSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return null;
      }

      const config = {
        token,
        url: `${apiOriginAggregator}/popup/${url}`,
        method,
        [method === 'POST' ? 'data' : 'params']:
          method === 'POST' ? JSON.stringify(payload) : payload,
      };

      try {
        const response = await makeRequest(config);

        if (onSuccess) {
          onSuccess(response);
        }

        return response;
      } catch (error) {
        toast.error(error.message);

        return null;
      } finally {
        if (disableSpinner) {
          setTabSpinner(false);
        }
      }
    },
    [apiOriginAggregator, getAccessTokenSilently, setTabSpinner],
  );

  useEffect(() => {
    (async () => {
      await Promise.allSettled([
        popupRequest({
          disableSpinner: false,
          url: 'list',
          onSuccess: response => setPopupList(response),
        }),
        getAggregators(),
      ]);
      setTabSpinner(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableColumns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        width: 400,
      },
      {
        key: 'isActive',
        header: '',
        width: 80,
        renderValue: value =>
          value ? (
            <Badge className="bo-inline-badge" bg="success">
              Active
            </Badge>
          ) : null,
      },
      {
        key: 'aggregators',
        header: '',
        width: 300,
        renderValue: (value, row) => {
          if (!row.isActive) {
            return null;
          }

          if (value.length > 0) {
            return (
              <div>
                <TableMultiLineText
                  value={value.map(x => (
                    <div key={x}>
                      {(aggregators.length > 0 && aggregators.find(agg => agg.id === x)?.name) ||
                        '---'}
                    </div>
                  ))}
                  showTooltip={false}
                  uniqueId={`popup_message_${row.id}`}
                  lineClamp={5}
                />
              </div>
            );
          }

          return 'All Aggregators';
        },
      },
      {
        key: 'edit',
        header: '',
        width: 50,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            onClick={() => {
              popupRequest({
                disableSpinner: false,
                url: 'get',
                payload: { id: row.id },
                onSuccess: response =>
                  setPopupMessage({
                    ...response,
                    activationPeriods: response.activationPeriods.map(x => ({
                      ...x,
                      tempId: crypto.randomUUID(),
                    })),
                  }),
              });
            }}
          >
            Edit
          </Button>
        ),
      },
      {
        key: 'duplicate',
        header: '',
        width: 80,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            onClick={async () => {
              const item = await popupRequest({
                disableSpinner: false,
                url: 'get',
                payload: { id: row.id },
              });

              await popupRequest({
                url: 'save',
                method: 'POST',
                payload: {
                  name: `Duplicate of '${item.name}'`,
                  maxShowCount: item.maxShowCount,
                  html: item.html,
                  activationPeriods: [],
                },
                onSuccess: response => {
                  setPopupList(prevList => [...prevList, response]);
                },
              });
            }}
          >
            Duplicate
          </Button>
        ),
      },
      {
        key: 'delete',
        header: '',
        width: 60,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            onClick={() =>
              popupRequest({
                url: 'delete',
                method: 'POST',
                payload: { id: row.id },
                onSuccess: () => setPopupList(prevList => prevList.filter(x => x.id !== row.id)),
              })
            }
          >
            Delete
          </Button>
        ),
      },
    ],
    [aggregators, popupRequest],
  );

  const getRowHeight = useCallback(
    index => {
      const aggregatorsRows = popupList[index]?.aggregators?.length || 0;
      const aggregatorsRowsHeight = aggregatorsRows * 18 + 18;

      return Math.max(aggregatorsRowsHeight >= 108 ? 108 : aggregatorsRowsHeight, 36);
    },
    [popupList],
  );

  return (
    <AutoSizer>
      {({ height, width }) => (
        <Container style={{ width }}>
          {popupMessage ? (
            <PopupMessage
              aggregators={aggregators}
              popupMessage={popupMessage}
              setPopupMessage={setPopupMessage}
              popupRequest={popupRequest}
              setPopupList={setPopupList}
              width={width - 24}
              setTabSpinner={setTabSpinner}
            />
          ) : (
            <>
              <Button
                className="bo-button mb-3"
                onClick={() => {
                  setTabSpinner(true);
                  setPopupMessage({
                    name: '',
                    maxShowCount: 3,
                    activationPeriods: [],
                    html: { en: '', es: '' },
                  });
                }}
              >
                Create New Campaign
              </Button>
              <VirtualizedList
                tableRows={popupList}
                tableColumns={tableColumns}
                width={width - 24}
                height={height - 72 - 54}
                getRowHeight={getRowHeight}
                rowKey="id"
              />
            </>
          )}
        </Container>
      )}
    </AutoSizer>
  );
}

export default PopupMessages;
